import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  input,
} from "@angular/core";
import { firstValueFrom } from "rxjs";
import { DialogsService } from "app/shared/services/dialogs/dialogs.service";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { ChipsComponent } from "../../modules/chips/chips.component";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslatePipe } from "../../pipes/translate.pipe";
import {
  SyncLinkApiService,
} from "app/features/connector/service/sync-link-api.service";
import { SyncObjectType } from "app/features/connector/models/sync-link.model";

/**
 * C15 warn-before-delete hint. When provided, the delete button pre-flights
 * a GET /sync-link/has-link request on click. If a SyncLink exists the
 * confirmation dialog warns the user that the element is tied to an external
 * connector (PrestaShop) and that deleting locally will not remove it there.
 */
export interface SyncLinkCheckInput {
  type: SyncObjectType;
  id: string;
  /** Display name of the entity, used in the warning message. */
  entityLabel?: string;
}

@Component({
  selector: "vl-delete-button",
  standalone: true,
  imports: [
    ChipsComponent,
    MatTooltip,
    MatIcon,
    MatProgressSpinner,
    TranslatePipe,
  ],
  templateUrl: "./delete-button.component.html",
  styleUrls: ["./delete-button.component.scss"],
})
export class DeleteButtonComponent {
  @Input() disabled: boolean;
  @Output() delete = new EventEmitter();
  size = input<"small" | "normal">("normal");
  loading = input(false);

  /**
   * G5 — when set, the button checks the SyncLink registry before showing the
   * confirmation dialog. If the entity is linked to a connector, the dialog
   * message is extended with a warning.
   */
  syncLinkCheck = input<SyncLinkCheckInput | null>(null);

  constructor(
    private dialogsService: DialogsService,
    private syncLinkApi: SyncLinkApiService,
  ) {}

  async onDelete() {
    let warning: string | undefined;
    const check = this.syncLinkCheck();
    if (check?.type && check?.id) {
      try {
        const res = await firstValueFrom(
          this.syncLinkApi.hasLink(check.type, check.id),
        );
        if (res.hasLink) {
          const label = check.entityLabel ?? "cet élément";
          warning =
            `${label} est lié à un connecteur e-commerce. ` +
            `La suppression dans Justup ne le retire pas de la boutique en ligne, ` +
            `et les futures modifications ne seront plus importées automatiquement.`;
        }
      } catch {
        // Best-effort: if the API call fails, we still show the standard dialog.
      }
    }

    const message = warning
      ? `${warning}\n\nConfirmez-vous la suppression ?`
      : "Vous êtes sur le point de supprimer cet élément. Confirmez-vous ?";

    const dialogRef = this.dialogsService.open(
      ConfirmationDialogComponent,
      "fit-content",
      "fit-content",
      {
        title: warning ? "Suppression (lien connecteur)" : "Suppression",
        message,
      },
    );

    const sub = dialogRef.afterClosed().subscribe((res: boolean) => {
      if (res) {
        this.delete.emit();
      }
      sub.unsubscribe();
    });
  }
}
